// src/routes/profile-routes.tsx
import React, { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
    FaUser,
    FaUserEdit,
    FaUsers,
    FaUserCircle,
    FaUserCog,
    FaUserCheck,
    FaChartLine,
    FaEye,
    FaBriefcase,
    FaHeart,
    FaCertificate,
    FaHistory,
    FaSearch
} from 'react-icons/fa';

// Lazy load profile components
const ProfileViewPage = lazy(() => import('@/pages/management/Profile/ProfileViewPage'));
const ProfileEditPage = lazy(() => import('@/pages/management/Profile/ProfileEditPage'));

// Profile tab components
const ProfileOverview = lazy(() => import('@/pages/management/Profile/tabs/ProfileOverview'));
const ProfileWorks = lazy(() => import('@/pages/management/Profile/tabs/ProfileWorks'));
//const ProfileLicenses = lazy(() => import('@/pages/management/Profile/tabs/ProfileLicenses'));
const ProfileCertificates = lazy(() => import('@/pages/management/Profile/tabs/ProfileCertificates'));
const ProfileActivity = lazy(() => import('@/pages/management/Profile/tabs/ProfileActivity'));
const ProfileAbout = lazy(() => import('@/pages/management/Profile/tabs/ProfileAbout'));

// Admin/Management components
const ProfileManagement = lazy(() => import('@/pages/management/Profile/ProfileManagement'));
const ProfileAnalytics = lazy(() => import('@/pages/management/Profile/ProfileAnalytics'));
const ProfileVerification = lazy(() => import('@/pages/management/Profile/ProfileVerification'));

// Profile routes array
export const profileRoutes: RoutesProps[] = [
    // Current user's profile routes
    {
        path: '/profile',
        name: 'Hồ sơ của tôi',
        element: <ProfileViewPage />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'],
        icon: FaUser,
    },
    {
        path: '/profile/edit',
        name: 'Chỉnh sửa hồ sơ',
        element: <ProfileEditPage />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'],
        icon: FaUserEdit,
    },

    // Other user's profile routes
    {
        path: '/user/:id',
        name: 'Hồ sơ người dùng',
        element: <ProfileViewPage />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaUserCircle,
    },
    {
        path: '/user/:id/overview',
        name: 'Tổng quan',
        element: <ProfileViewPage initialTab="overview" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaChartLine,
    },
    {
        path: '/user/:id/works',
        name: 'Tác phẩm',
        element: <ProfileViewPage initialTab="works" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaBriefcase,
    },
    {
        path: '/user/:id/licenses',
        name: 'Giấy phép',
        element: <ProfileViewPage initialTab="licenses" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaCertificate,
    },
    {
        path: '/user/:id/certificates',
        name: 'Chứng chỉ',
        element: <ProfileViewPage initialTab="certificates" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaUserCheck,
    },
    {
        path: '/user/:id/activity',
        name: 'Hoạt động',
        element: <ProfileViewPage initialTab="activity" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'],
        icon: FaHistory,
    },
    {
        path: '/user/:id/about',
        name: 'Giới thiệu',
        element: <ProfileViewPage initialTab="about" />,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaEye,
    },

    // Profile management routes (for admins and staff)
    {
        path: '/management/profiles',
        name: 'Quản lý hồ sơ',
        element: <ProfileManagement />,
        roles: ['admin', 'staff', 'support_specialist'],
        icon: FaUsers,
    },
    {
        path: '/management/profiles/analytics',
        name: 'Phân tích hồ sơ',
        element: <ProfileAnalytics />,
        roles: ['admin', 'staff', 'monitor', 'support_specialist'],
        icon: FaChartLine,
    },
    {
        path: '/management/profiles/verification',
        name: 'Xác thực hồ sơ',
        element: <ProfileVerification />,
        roles: ['admin', 'staff', 'legal'],
        icon: FaUserCheck,
    },

    // Public profile discovery
    {
        path: '/profiles',
        name: 'Khám phá hồ sơ',
        element: <ProfileViewPage />, // Will show profile discovery page
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
        icon: FaSearch,
    },
];

// Navigation structure for profile module
export const profileNavigationStructure = {
    // Current user profile
    myProfile: {
        key: 'my-profile',
        translationKey: 'myProfile',
        path: '/profile',
        icon: FaUser,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'],
    },
    editProfile: {
        key: 'edit-profile',
        translationKey: 'editProfile',
        path: '/profile/edit',
        icon: FaUserEdit,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'],
    },

    // Profile discovery
    profileDiscovery: {
        key: 'profile-discovery',
        translationKey: 'profileDiscovery',
        path: '/profiles',
        icon: FaSearch,
        roles: ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist', 'guest'],
    },

    // Profile management (admin/staff)
    profileManagement: {
        key: 'profile-management',
        translationKey: 'profileManagement',
        icon: FaUsers,
        roles: ['admin', 'staff', 'support_specialist'],
        subitems: [
            {
                key: 'profile-list',
                translationKey: 'profileList',
                path: '/management/profiles',
                icon: FaUsers,
                roles: ['admin', 'staff', 'support_specialist'],
            },
            {
                key: 'profile-analytics',
                translationKey: 'profileAnalytics',
                path: '/management/profiles/analytics',
                icon: FaChartLine,
                roles: ['admin', 'staff', 'monitor', 'support_specialist'],
            },
            {
                key: 'profile-verification',
                translationKey: 'profileVerification',
                path: '/management/profiles/verification',
                icon: FaUserCheck,
                roles: ['admin', 'staff', 'legal'],
            },
        ],
    },
};

// Helper function to get profile routes for a user role
export const getProfileRoutesForRole = (userRoles: string[]): RoutesProps[] => {
    return profileRoutes.filter((route) => {
        if (!route.roles || route.roles.length === 0) return true;
        return route.roles.some((role) => userRoles.includes(role));
    });
};

// Helper function to get profile navigation for a user role
export const getProfileNavigationForRole = (userRoles: string[]) => {
    const navigation: any[] = [];

    Object.entries(profileNavigationStructure).forEach(([key, item]) => {
        if (item.roles && item.roles.some((role) => userRoles.includes(role))) {
            // Check if it has subitems (like profile management)
            if ('subitems' in item && item.subitems) {
                const filteredSubitems = item.subitems.filter((subitem: any) =>
                    subitem.roles.some((role: string) => userRoles.includes(role))
                );

                if (filteredSubitems.length > 0) {
                    navigation.push({
                        ...item,
                        subitems: filteredSubitems,
                    });
                }
            } else {
                navigation.push(item);
            }
        }
    });

    return navigation;
};

// Helper function to check if user has access to profile features
export const hasAccessToProfiles = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'];
    return userRoles.some(role => allowedRoles.includes(role));
};

// Helper function to check if user can edit their own profile
export const canEditOwnProfile = (userRoles: string[]): boolean => {
    const editRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'];
    return userRoles.some(role => editRoles.includes(role));
};

// Helper function to check if user can manage other profiles
export const canManageProfiles = (userRoles: string[]): boolean => {
    const managementRoles = ['admin', 'staff', 'support_specialist'];
    return userRoles.some(role => managementRoles.includes(role));
};

// Helper function to check if user can verify profiles
export const canVerifyProfiles = (userRoles: string[]): boolean => {
    const verificationRoles = ['admin', 'staff', 'legal'];
    return userRoles.some(role => verificationRoles.includes(role));
};

// Helper function to check if user can view profile analytics
export const canViewProfileAnalytics = (userRoles: string[]): boolean => {
    const analyticsRoles = ['admin', 'staff', 'monitor', 'support_specialist'];
    return userRoles.some(role => analyticsRoles.includes(role));
};

// Helper function to check if user can view other profiles
export const canViewOtherProfiles = (userRoles: string[]): boolean => {
    // Everyone including guests can view public profiles
    return true;
};

// Helper function to check if user can follow other users
export const canFollowUsers = (userRoles: string[]): boolean => {
    const followRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'];
    return userRoles.some(role => followRoles.includes(role));
};

// Helper function to check if user can message other users
export const canMessageUsers = (userRoles: string[]): boolean => {
    const messageRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'teacher', 'student', 'business_developer', 'security_analyst', 'support_specialist'];
    return userRoles.some(role => messageRoles.includes(role));
};

// Profile menu groups for different user types
export const getProfileMenuGroups = (userRoles: string[]) => {
    const menuGroups: any = {};

    // My Profile (for authenticated users)
    if (hasAccessToProfiles(userRoles)) {
        menuGroups.myProfile = {
            key: 'my-profile-group',
            translationKey: 'myProfileGroup',
            icon: FaUser,
            children: [
                profileNavigationStructure.myProfile,
                profileNavigationStructure.editProfile,
            ].filter(item => item.roles.some(role => userRoles.includes(role)))
        };
    }

    // Profile Discovery (for everyone including guests)
    menuGroups.discovery = {
        key: 'profile-discovery-group',
        translationKey: 'profileDiscoveryGroup',
        icon: FaSearch,
        children: [profileNavigationStructure.profileDiscovery]
    };

    // Profile Management (for admins and staff)
    if (canManageProfiles(userRoles)) {
        menuGroups.management = profileNavigationStructure.profileManagement;
    }

    return menuGroups;
};

// Profile tab permissions
export const getProfileTabPermissions = (userRoles: string[], isOwnProfile: boolean = false) => {
    return {
        overview: true, // Everyone can see overview
        works: true, // Everyone can see public works
        licenses: isOwnProfile || canViewProfileAnalytics(userRoles), // Own profile or admin
        certificates: true, // Everyone can see public certificates
        activity: isOwnProfile || canViewProfileAnalytics(userRoles), // Own profile or admin
        about: true, // Everyone can see about section
    };
};

// Profile action permissions
export const getProfileActionPermissions = (userRoles: string[], isOwnProfile: boolean = false) => {
    return {
        edit: isOwnProfile && canEditOwnProfile(userRoles),
        follow: !isOwnProfile && canFollowUsers(userRoles),
        message: !isOwnProfile && canMessageUsers(userRoles),
        report: !isOwnProfile && hasAccessToProfiles(userRoles),
        verify: canVerifyProfiles(userRoles),
        manage: canManageProfiles(userRoles),
        viewPrivateInfo: isOwnProfile || canManageProfiles(userRoles),
    };
};